import { ActionIcon, Avatar, Button, Group, Text } from '@mantine/core';
import { useModals } from '@mantine/modals';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RecordBankAccountBalanceModal } from 'src/Components/Modals/RecordBankAccountBalanceModal';
import { RowActions } from 'src/Components/Shared/RowActions';
import { useDeleteBankAccount } from 'src/Hooks/Mutations/useDeleteBankAccount';
import { useDeleteBankAccountBalance } from 'src/Hooks/Mutations/useDeleteBankAccountBalance';
import useFetchBankAccountBalances from 'src/Hooks/Queries/useFetchBankAccountBalances';
import { useHasPermission } from 'src/Hooks/Shared/useHasPermission';
import useNavigateKeepingSearch from 'src/Hooks/Shared/useNavigateKeepingSearch';

import styles from './BankAccountDetails.module.scss';
import { Props } from './BankAccountDetails.types';

const BankAccountDetails = ({ bankAccount, onDelete }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigateKeepingSearch();
  const modals = useModals();
  const { mutate: deleteBankAccount } = useDeleteBankAccount({ onSuccess: onDelete });
  const { data: balances, isLoading: isLoadingBalances } = useFetchBankAccountBalances(
    bankAccount.id
  );
  const { mutate: deleteBalance } = useDeleteBankAccountBalance();
  const { hasPermission: canRecordBalance } = useHasPermission('financeAdmin', 'full');
  const [isRecordingBalance, setIsRecordingBalance] = useState(false);

  const handleDeleteBalance = (balanceId: string) => {
    modals.openConfirmModal({
      title: t('bankAccounts.balance.delete.confirmTitle', 'Supprimer le solde'),
      children: (
        <Text size="sm">
          {t(
            'bankAccounts.balance.delete.confirmMessage',
            'Êtes-vous sûr de vouloir supprimer ce solde ? Cette action est irréversible.'
          )}
        </Text>
      ),
      labels: {
        confirm: t('common.delete', 'Supprimer'),
        cancel: t('common.cancel', 'Annuler'),
      },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteBalance({ bankAccountId: bankAccount.id, balanceId }),
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.line}>
        <div className={styles.title}>{bankAccount.name}</div>
        <RowActions
          actions={[
            {
              key: 'edit',
              label: t('bankAccounts.menu.edit', 'Modifier'),
              icon: <IconPencil size={18} />,
              onClick: () => navigate(`edit`, { state: { disableAnimation: true }, replace: true }),
            },
            {
              key: 'delete',
              label: t('bankAccounts.menu.delete', 'Supprimer'),
              icon: <IconTrash size={18} />,
              variant: 'danger',
              onClick: () => deleteBankAccount(bankAccount.id),
            },
          ]}
        />
      </div>

      <div className={styles.subtitle}>
        {t('bankAccounts.balance.history.title', 'Historique des soldes')}
      </div>

      {!isLoadingBalances && (!balances || balances.length === 0) && (
        <Text size="sm" c="dimmed">
          {t('bankAccounts.balance.history.empty', 'Aucun solde enregistré.')}
        </Text>
      )}

      <div className={styles.balanceHistory}>
        {balances?.map(balance => (
          <div key={balance.id} className={styles.balanceLine}>
            <div className={styles.balanceDate}>
              {dayjs(balance.recordedAt).format('DD/MM/YYYY')}
            </div>

            <div
              className={balance.amount < 0 ? styles.balanceAmountNegative : styles.balanceAmount}
            >
              {balance.amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
            </div>

            <Group gap={6} className={styles.balanceAuthor}>
              {balance.recordedBy ? (
                <>
                  <Avatar
                    size={24}
                    color="initials"
                    name={`${balance.recordedBy.firstName} ${balance.recordedBy.lastName}`}
                  />
                  <span>{balance.recordedBy.firstName}</span>
                </>
              ) : (
                '—'
              )}
            </Group>

            {canRecordBalance && (
              <ActionIcon variant="subtle" onClick={() => handleDeleteBalance(balance.id)}>
                <IconTrash size={16} />
              </ActionIcon>
            )}
          </div>
        ))}
      </div>

      {canRecordBalance && (
        <Button variant="outline" onClick={() => setIsRecordingBalance(true)}>
          {t('bankAccounts.balance.record', 'Saisir un solde')}
        </Button>
      )}

      <RecordBankAccountBalanceModal
        opened={isRecordingBalance}
        bankAccountId={bankAccount.id}
        onClose={() => setIsRecordingBalance(false)}
      />
    </div>
  );
};

export default BankAccountDetails;
